// Card-4.10 (2026-04-29) — Vuex module para administração de
// PipelineSequenceDefinitions. State machine simples: list+selected+loading.
// Componentes admin (Index/Form) consomem via getters/dispatch.

import PipelineSequenceDefinitionsAPI from 'dashboard/api/pipelineSequenceDefinitions';

const normalizeAccountId = accountId => {
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};

const requireAccountId = (rootGetters, accountId) => {
  const resolvedAccountId = normalizeAccountId(
    accountId === undefined ? rootGetters.getCurrentAccountId : accountId
  );
  if (resolvedAccountId === null) {
    throw new TypeError('accountId is required');
  }
  return resolvedAccountId;
};

const accountIsCurrent = (rootGetters, accountId) =>
  normalizeAccountId(rootGetters.getCurrentAccountId) === accountId;

const prepareAccountState = (commit, localState, accountId) => {
  const hasUnscopedData =
    localState.loadedAccountId === null &&
    (localState.records.length > 0 || localState.selected !== null);
  if (
    hasUnscopedData ||
    (localState.loadedAccountId !== null &&
      localState.loadedAccountId !== accountId)
  ) {
    commit('RESET_ACCOUNT_STATE');
  }
  commit('SET_ACTIVE_ACCOUNT', accountId);
  return localState.requestEpoch;
};

const EMPTY_STATUS_COUNTS = { all: 0, active: 0, inactive: 0 };

// The response uses `total` (see `pagination_meta` in the controller). The old
// `total_count` key here was a placeholder that never matched the payload.
const emptyMeta = () => ({
  current_page: 1,
  total: 0,
  total_pages: 1,
  status_counts: { ...EMPTY_STATUS_COUNTS },
});

const state = {
  requestEpoch: 0,
  loadedAccountId: null,
  records: [],
  selected: null,
  loading: false,
  // Kept apart from `loading` on purpose: the detail panel fetches while the
  // list stays on screen, and sharing the flag would blank the whole table.
  detailLoading: false,
  saving: false,
  error: null,
  meta: emptyMeta(),
  analytics: null,
  analyticsLoading: false,
};

const getters = {
  getSequenceDefinitions: _state => _state.records,
  getSequenceDefinitionById: _state => id =>
    _state.records.find(d => d.id === Number(id)),
  getSelectedSequenceDefinition: _state => _state.selected,
  getSequenceDefinitionsMeta: _state => _state.meta,
  getSequenceDefinitionsStatusCounts: _state =>
    _state.meta?.status_counts || { ...EMPTY_STATUS_COUNTS },
  isFetchingSequenceDefinitions: _state => _state.loading,
  isFetchingSequenceDefinitionDetail: _state => _state.detailLoading,
  isSavingSequenceDefinition: _state => _state.saving,
  getSequenceDefinitionsError: _state => _state.error,
  getSequenceAnalytics: _state => _state.analytics,
  isFetchingSequenceAnalytics: _state => _state.analyticsLoading,
};

const actions = {
  async fetch(
    { commit, rootGetters, state: localState },
    { accountId: rawAccountId, ...params } = {}
  ) {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    const requestIsCurrent = () =>
      requestEpoch === localState.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.list(
        params,
        accountId
      );
      if (!requestIsCurrent()) return [];
      commit('SET_RECORDS', data.data || []);
      commit('SET_META', data.meta || {});
      return data.data || [];
    } catch (e) {
      if (!requestIsCurrent()) return [];
      commit('SET_ERROR', e.response?.data?.error || e.message);
      return [];
    } finally {
      if (requestIsCurrent()) commit('SET_LOADING', false);
    }
  },

  async fetchOne({ commit, rootGetters, state: localState }, rawPayload) {
    const payload =
      rawPayload !== null && typeof rawPayload === 'object'
        ? rawPayload
        : { id: rawPayload };
    const accountId = requireAccountId(rootGetters, payload.accountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    const requestIsCurrent = () =>
      requestEpoch === localState.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);
    commit('SET_DETAIL_LOADING', true);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.show(
        payload.id,
        accountId
      );
      if (!requestIsCurrent()) return null;
      commit('SET_SELECTED', data.data);
      return data.data;
    } catch (e) {
      // Without this the caller gets an unhandled rejection and the panel is
      // stuck on its skeleton with no way to explain itself.
      if (requestIsCurrent()) {
        commit('SET_ERROR', e.response?.data?.error || e.message);
      }
      return null;
    } finally {
      if (requestIsCurrent()) commit('SET_DETAIL_LOADING', false);
    }
  },

  async fetchAnalytics(
    { commit, rootGetters, state: localState },
    payload = {}
  ) {
    const accountId = requireAccountId(rootGetters, payload.accountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    const requestIsCurrent = () =>
      requestEpoch === localState.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);
    commit('SET_ANALYTICS_LOADING', true);
    try {
      const { data } =
        await PipelineSequenceDefinitionsAPI.analytics(accountId);
      if (!requestIsCurrent()) return null;
      commit('SET_ANALYTICS', data.data || null);
      return data.data || null;
    } catch {
      // The strip is a secondary read: a failure blanks the numbers, it never
      // takes the list down with it.
      if (requestIsCurrent()) commit('SET_ANALYTICS', null);
      return null;
    } finally {
      if (requestIsCurrent()) commit('SET_ANALYTICS_LOADING', false);
    }
  },

  async create(
    { commit, rootGetters, state: localState },
    { accountId: rawAccountId, ...payload }
  ) {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    const requestIsCurrent = () =>
      requestEpoch === localState.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.create(
        payload,
        accountId
      );
      if (!requestIsCurrent()) return null;
      commit('UPSERT_RECORD', data.data);
      return data.data;
    } catch (e) {
      if (!requestIsCurrent()) return null;
      const msg =
        e.response?.data?.details?.join(', ') ||
        e.response?.data?.error ||
        e.message;
      commit('SET_ERROR', msg);
      throw e;
    } finally {
      if (requestIsCurrent()) commit('SET_SAVING', false);
    }
  },

  async update(
    { commit, rootGetters, state: localState },
    { id, payload, accountId: rawAccountId }
  ) {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    const requestIsCurrent = () =>
      requestEpoch === localState.requestEpoch &&
      accountIsCurrent(rootGetters, accountId);
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.update(
        id,
        payload,
        accountId
      );
      if (!requestIsCurrent()) return null;
      commit('UPSERT_RECORD', data.data);
      commit('SET_SELECTED', data.data);
      return data.data;
    } catch (e) {
      if (!requestIsCurrent()) return null;
      const msg =
        e.response?.data?.details?.join(', ') ||
        e.response?.data?.error ||
        e.message;
      commit('SET_ERROR', msg);
      throw e;
    } finally {
      if (requestIsCurrent()) commit('SET_SAVING', false);
    }
  },

  async delete({ commit, rootGetters, state: localState }, rawPayload) {
    const payload =
      rawPayload !== null && typeof rawPayload === 'object'
        ? rawPayload
        : { id: rawPayload };
    const accountId = requireAccountId(rootGetters, payload.accountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    await PipelineSequenceDefinitionsAPI.delete(payload.id, accountId);
    if (
      requestEpoch === localState.requestEpoch &&
      accountIsCurrent(rootGetters, accountId)
    ) {
      commit('REMOVE_RECORD', payload.id);
    }
  },

  async toggleActive(
    { commit, rootGetters, state: localState },
    { id, active, accountId: rawAccountId }
  ) {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    const action = active ? 'activate' : 'deactivate';
    const { data } = await PipelineSequenceDefinitionsAPI[action](
      id,
      accountId
    );
    if (
      requestEpoch === localState.requestEpoch &&
      accountIsCurrent(rootGetters, accountId)
    ) {
      commit('UPSERT_RECORD', data.data);
    }
  },

  async duplicate(
    { commit, rootGetters, state: localState },
    { id, newName, accountId: rawAccountId }
  ) {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = prepareAccountState(commit, localState, accountId);
    const { data } = await PipelineSequenceDefinitionsAPI.duplicate(
      id,
      newName,
      accountId
    );
    if (
      requestEpoch !== localState.requestEpoch ||
      !accountIsCurrent(rootGetters, accountId)
    ) {
      return null;
    }
    commit('UPSERT_RECORD', data.data);
    return data.data;
  },

  reset({ commit }) {
    commit('RESET_ACCOUNT_STATE');
  },
};

const mutations = {
  SET_ACTIVE_ACCOUNT(_state, accountId) {
    _state.loadedAccountId = accountId;
  },
  SET_LOADING(_state, v) {
    _state.loading = v;
  },
  SET_DETAIL_LOADING(_state, v) {
    _state.detailLoading = v;
  },
  SET_ANALYTICS_LOADING(_state, v) {
    _state.analyticsLoading = v;
  },
  SET_ANALYTICS(_state, analytics) {
    _state.analytics = analytics;
  },
  SET_SAVING(_state, v) {
    _state.saving = v;
  },
  SET_ERROR(_state, v) {
    _state.error = v;
  },
  SET_RECORDS(_state, records) {
    _state.records = records;
  },
  SET_META(_state, meta) {
    _state.meta = { ..._state.meta, ...meta };
  },
  SET_SELECTED(_state, record) {
    _state.selected = record;
  },
  UPSERT_RECORD(_state, record) {
    const idx = _state.records.findIndex(r => r.id === record.id);
    if (idx >= 0) _state.records.splice(idx, 1, record);
    else _state.records.unshift(record);
  },
  REMOVE_RECORD(_state, id) {
    _state.records = _state.records.filter(r => r.id !== id);
    if (_state.selected?.id === id) _state.selected = null;
  },
  RESET_ACCOUNT_STATE(_state) {
    _state.requestEpoch += 1;
    _state.loadedAccountId = null;
    _state.records = [];
    _state.selected = null;
    _state.loading = false;
    _state.detailLoading = false;
    _state.saving = false;
    _state.error = null;
    _state.meta = emptyMeta();
    _state.analytics = null;
    _state.analyticsLoading = false;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
